// packages import
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import { Box, CircularProgress, IconButton, Link, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { teal } from "@mui/material/colors";
import axios, { isAxiosError } from 'axios';
import { FC, useContext, useEffect, useState } from "react";
import { useDropzone } from 'react-dropzone';
// component import
import { Alert } from "./Alert";
import { CustomModal } from "./CustomModal";
import { StickyTable } from "./StickyTable";
// others
import { DocumentIcon, DocumentUploadedIcon } from "../../assets/images";
import { AUTH_TOKEN, CASES_UPLOAD_CSV_TABLE_HEADERS, CONFLICT_EXCEPTION, CSV_FORMAT_INCORRECT, PHONE_FORMAT_NOT_CORRECT, USER_ALREADY_EXIST } from "../../constants";
import { AppContext } from "../../context";
import { ActionType } from "../../context/AppContextReducer";
import { UploadCsvProps } from "../../interfaceTypes";
import { Container, DocumentBox, UploadCsvTopBar } from "../../theme/styleComponents";
import { TextWrap } from "../../theme/styleConstants";
import { titleize } from "../../utils";

export const UploadCsv: FC<UploadCsvProps> = ({ isCase, refetchCases, refetchUsers }) => {
  const { isCsvUpload, dispatch } = useContext(AppContext)
  const [uploaded, setIsUploaded] = useState<boolean>(false)
  const [files, setFiles] = useState<File[]>([])
  const [errors, setErrors] = useState([])
  const token = localStorage.getItem(AUTH_TOKEN);
  const downloadHref = isCase ? `${process.env.PUBLIC_URL}/csvs/sampleCasesTemplate.csv` : `${process.env.PUBLIC_URL}/csvs/sampleAttorneysTemplate.csv`

  const handleClose = () => {
    if (isCsvUpload) {
      dispatch({ type: ActionType.SET_IS_CSV_Upload, isCsvUpload: !isCsvUpload })
      isCase && setErrors([])
      setFiles([])
    }
  }

  const onDrop = async (acceptedFiles: File[]) => {
    isCase && setErrors([])
    setFiles(acceptedFiles.map(file => Object.assign(file, {
      preview: URL.createObjectURL(file)
    })));

    const formData = new FormData();
    if (acceptedFiles.length !== 0) {
      formData.append("title", acceptedFiles[0].name);
      formData.append("file", acceptedFiles[0]);
      setIsUploaded(true)

      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}${isCase ? '/cases/createFromCsv' : '/users/createFromCsv'}`,
          formData,
          {
            headers: {
              authorization: `Bearer ${token}`
            }
          }
        )

        const { data } = response
        const { response: { status }, errors } = data

        if (status === 200 && ((isCase && errors.length === 0) || !isCase)) {
          setIsUploaded(false)
          Alert.success(isCase ? 'Cases created successfully' : 'Users created successfully');
          isCase && refetchCases && refetchCases();
          !isCase && refetchUsers && refetchUsers();
          setFiles([])
          isCsvUpload && dispatch({ type: ActionType.SET_IS_CSV_Upload, isCsvUpload: !isCsvUpload })
        } else {
          setIsUploaded(false)
          isCase && setErrors(errors)
          isCase && refetchCases && refetchCases();
        }
      }

      catch (error) {
        setIsUploaded(false)
        setFiles([])

        if (isAxiosError(error)) {
          const { response: { status, data } = {} } = error;
          const { error: errorText } = data || {}
          const { message } = errorText || {}

          if (status === 400 && !message) {
            const caseNumber = data && errorText && errorText?.match(/(\d|,)+/g).pop();

            if (isCase) {
              Alert.error(`Matter number: ${caseNumber} already exist`);
            } else {
              Alert.error(USER_ALREADY_EXIST);
            }
          }

          if (status === 400 && message && isCase) {
            Alert.error(message);
          }

          if (status === 409 && error) {
            const { response } = error || {}
            const { data } = response || {}
            const { message: errorMessage } = data || {}

            if (errorMessage === CONFLICT_EXCEPTION) {
              Alert.error(USER_ALREADY_EXIST);
            } else {
              if (!errorMessage) {
                Alert.error(CSV_FORMAT_INCORRECT);
              } else {
                Alert.error(errorMessage);
              }
            }
          }

          if (status === 500 && error) {
            const { response } = error || {}
            const { data } = response || {}
            const { message: errorMessage } = data || {}

            if (errorMessage === PHONE_FORMAT_NOT_CORRECT) {
              Alert.error(PHONE_FORMAT_NOT_CORRECT);
            }
          }
        }
      }
    }
  }
  const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject, fileRejections } = useDropzone({
    onDrop: onDrop,
    accept: {
      'file/csv': ['.csv'],
    },
  });

  useEffect(() => {
    fileRejections.map(({ errors }) => (
      errors.map((messages) => {
        const { message } = messages || {}

        return Alert.error(message)
      }))
    )
  }, [fileRejections])

  return (
    <CustomModal title='Upload CSV' isOpen={isCsvUpload} handleClose={handleClose} maxWidth='xl'>
      <Typography mb={2}>Please download sample csv from <Link href={downloadHref}>here</Link>.</Typography>

      <Box className="container" sx={{ cursor: 'pointer', pointerEvents: uploaded ? 'none' : 'unset' }}>
        <Container {...getRootProps({ isFocused, isDragAccept, isDragReject })}>
          <input {...getInputProps()} type="file" />

          <IconButton className="uploadButton">
            <DocumentIcon />
          </IconButton>

          <Typography variant="body2" color='darkOne' fontWeight="400" margin="8px 0px">
            <Box component='span' color='darkRed' sx={{ textDecoration: 'underline' }}>Click to upload</Box> or drag and drop
          </Typography>

          <Typography variant="subtitle1" color='lightGrayOne'>CSV (max. 3 MB)</Typography>
        </Container>
      </Box>

      <Box mb='20px'>
        {files.map((file, index) => {
          const { name } = file || {}

          return (
            <DocumentBox key={index}>
              <Box display="flex" alignItems="center">
                <IconButton className="MuiIconButton-document">
                  <DocumentIcon />
                </IconButton>

                <Box paddingLeft={2}>
                  <Typography variant="body1" fontSize="16px" letterSpacing="0.17px">{name.replace(/ /g, '-')}</Typography>
                </Box>
              </Box>

              {uploaded
                ?
                <Box display="flex" alignItems="center">
                  <Box marginRight={2} marginTop="5px">
                    <CircularProgress size={35} sx={{ color: teal[100] }} />
                  </Box>
                </Box>
                :
                (errors.length > 0
                  ?
                  <Box display="flex" alignItems="center">
                    <IconButton className="MuiIconButton-document">
                      <ErrorIcon color="primary" />
                    </IconButton>
                  </Box>
                  :
                  <Box display="flex" alignItems="center">
                    <IconButton className="MuiIconButton-document">
                      <DocumentUploadedIcon />
                    </IconButton>
                  </Box>
                )
              }
            </DocumentBox>
          )
        })}
      </Box>

      {isCase && errors.length !== 0 &&
        <>
          <UploadCsvTopBar borderRadius={1} p={1} mb={3} gap={1}>
            <WarningIcon sx={{ color: 'white' }} />
            <Typography variant='body1' textAlign='center' color='white'>
              All cases successfully imported except these, please check the following rows for errors:
            </Typography>
          </UploadCsvTopBar>

          <StickyTable height='520px'>
            <TableHead>
              <TableRow>
                {CASES_UPLOAD_CSV_TABLE_HEADERS.map((item, index) => {
                  return (
                    <TableCell key={`${item}-${index}`} component="th" sx={{ minWidth: '150px ' }}>
                      {item}
                    </TableCell>
                  )
                })}
              </TableRow>
            </TableHead>

            <TableBody>
              {errors?.map((item) => {
                const { row, errorDetail } = item || {}
                const { clientId } = row || {}
                const { attorneyUserError, classError, paralegalUserError, caseAlreadyExistError, clientError, matterIdError } = errorDetail || {}

                return (
                  <TableRow key={clientId}>
                    <TableCell>
                      <Typography variant="body1" sx={TextWrap}>{clientId || "--"}</Typography>
                      {isCase && attorneyUserError && (
                        <TableRow sx={{ border: "1px solid red" }}>
                          <Box display='flex' paddingTop={1} gap={1}>
                            <Typography variant="subtitle2" display="inline" whiteSpace='nowrap' sx={{ color: "red" }}>
                              Error:
                            </Typography>

                            <Typography variant="body1" display="inline" whiteSpace='nowrap'>
                              {titleize(attorneyUserError || "")}
                            </Typography>
                          </Box>
                        </TableRow>
                      )}

                      {isCase && caseAlreadyExistError && (
                        <TableRow sx={{ border: "1px solid red" }}>
                          <Box display='flex' paddingTop={1} gap={1}>
                            <Typography variant="subtitle2" display="inline" whiteSpace='nowrap' sx={{ color: "red" }}>
                              Error:
                            </Typography>

                            <Typography variant="body1" display="inline" whiteSpace='nowrap'>
                              {titleize(caseAlreadyExistError || "")}
                            </Typography>
                          </Box>
                        </TableRow>
                      )}

                      {isCase && classError && (
                        <TableRow sx={{ borderBottom: "1px solid red" }}>
                          <Box display='flex' paddingTop={1} gap={1}>
                            <Typography variant="subtitle2" display="inline" sx={{ color: "red" }} whiteSpace='nowrap'>
                              Error:
                            </Typography>

                            <Typography variant="body1" display="inline" whiteSpace='nowrap'>
                              {titleize(classError || "")}
                            </Typography>
                          </Box>
                        </TableRow>
                      )}

                      {isCase && paralegalUserError && (
                        <TableRow sx={{ border: "1px solid red" }}>
                          <Box display='flex' paddingTop={1} gap={1}>
                            <Typography variant="subtitle2" display="inline" sx={{ color: "red" }} whiteSpace='nowrap'>
                              Error:
                            </Typography>

                            <Typography variant="body1" display="inline" whiteSpace='nowrap'>
                              {titleize(paralegalUserError || "")}
                            </Typography>
                          </Box>
                        </TableRow>
                      )}

                      {isCase && matterIdError && (
                        <TableRow sx={{ border: "1px solid red" }}>
                          <Box display='flex' paddingTop={1} gap={1}>
                            <Typography variant="subtitle2" display="inline" sx={{ color: "red" }} whiteSpace='nowrap'>
                              Error:
                            </Typography>

                            <Typography variant="body1" display="inline" whiteSpace='nowrap'>
                              {titleize(matterIdError || "")}
                            </Typography>
                          </Box>
                        </TableRow>
                      )}

                      {isCase && clientError && (
                        <TableRow sx={{ border: "1px solid red" }}>
                          <Box display='flex' paddingTop={1} gap={1}>
                            <Typography variant="subtitle2" display="inline" sx={{ color: "red" }} whiteSpace='nowrap'>
                              Error:
                            </Typography>

                            <Typography variant="body1" display="inline" whiteSpace='nowrap'>
                              {titleize(clientError || "")}
                            </Typography>
                          </Box>
                        </TableRow>
                      )}
                    </TableCell>

                    {CASES_UPLOAD_CSV_TABLE_HEADERS.slice(1).map((value, index) => (
                      <TableCell sx={{ verticalAlign: 'baseLine' }} key={`${value}-${index}`}>
                        <Typography variant="body1" sx={TextWrap}>{row[value] || "--"}</Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                )
              })}
            </TableBody>
          </StickyTable>
        </>
      }
    </CustomModal>
  )
}